/**
 * Pure helpers for grouping/bucketing the merged notification +
 * conversation feed. Platform-free — no `browser.*`, no `window.*`,
 * no i18n. The date-label helper takes a labels bag for "Today" and
 * "Yesterday" so it stays free of any specific i18n stack.
 */

import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";

export interface AppGroup {
  app: string;
  items: DecryptedNotification[];
}

/**
 * Group notifications by `appPackage`. Returns groups keyed by
 * `appPackage` with a display name (the first notification's
 * `app` field — Android sends a stable display name per package).
 *
 * Order within each group preserves input order; the caller is
 * responsible for sorting beforehand.
 */
export function groupByApp(notifications: DecryptedNotification[]): Map<string, AppGroup> {
  const groups = new Map<string, AppGroup>();
  for (const n of notifications) {
    const existing = groups.get(n.appPackage);
    if (existing) {
      existing.items.push(n);
    } else {
      groups.set(n.appPackage, { app: n.app, items: [n] });
    }
  }
  return groups;
}

export interface DateLabels {
  today: string;
  yesterday: string;
}

/**
 * Return a human-readable date label for a timestamp:
 *   - same day → labels.today (e.g. "Today")
 *   - previous day → labels.yesterday (e.g. "Yesterday")
 *   - older → locale-formatted "Mon DD" (or "Mon DD YYYY" if not
 *     same year), via `Date.toLocaleDateString(undefined, ...)`.
 *
 * `Date.now()` is read internally to compute the day boundaries —
 * call sites that need deterministic output should mock the clock
 * or pass a fixed timestamp pinned to the relevant day.
 */
export function getDateLabel(timestamp: number, labels: DateLabels): string {
  const now = new Date();
  const date = new Date(timestamp);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86_400_000;

  if (timestamp >= todayStart) return labels.today;
  if (timestamp >= yesterdayStart) return labels.yesterday;
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export type TimelineItem =
  | { kind: "notification"; data: DecryptedNotification }
  | { kind: "conversation"; data: ConversationThread };

export interface TimelineBucket {
  label: string;
  items: TimelineItem[];
}

/**
 * Merge regular notifications and conversation threads into a single
 * timestamp-descending list, then group into date buckets (Today /
 * Yesterday / named days). Stable for downstream `.map()` keys: the
 * inner items keep their original `msgId` / thread `key`.
 */
export function buildTimelineBuckets(
  notifications: DecryptedNotification[],
  conversations: ConversationThread[],
  labels: DateLabels
): TimelineBucket[] {
  const merged: TimelineItem[] = [
    ...notifications.map((n): TimelineItem => ({ kind: "notification", data: n })),
    ...conversations.map((c): TimelineItem => ({ kind: "conversation", data: c })),
  ].sort((a, b) => {
    const ta = a.kind === "notification" ? a.data.timestamp : a.data.lastMessageTimestamp;
    const tb = b.kind === "notification" ? b.data.timestamp : b.data.lastMessageTimestamp;
    return tb - ta;
  });

  const buckets: TimelineBucket[] = [];
  let currentLabel = "";
  for (const item of merged) {
    const ts = item.kind === "notification" ? item.data.timestamp : item.data.lastMessageTimestamp;
    const label = getDateLabel(ts, labels);
    if (label !== currentLabel) {
      currentLabel = label;
      buckets.push({ label, items: [item] });
    } else {
      buckets[buckets.length - 1].items.push(item);
    }
  }
  return buckets;
}

export interface PrivacyAppGroup {
  /** App package key — stable React key + recency-sort identity. */
  packageKey: string;
  /** App display name (from the first item's `app` field). */
  appName: string;
  /** Total items for the app = calls + regular notifications + conversation threads. */
  count: number;
  /** Drives the count noun: all-conversation → messages, all-call → calls, else notifications. */
  labelKind: "messages" | "calls" | "notifications";
  /** Newest item timestamp for the app — descending sort key. */
  latestTs: number;
}

interface PrivacyAccumulator {
  appName: string;
  calls: number;
  regular: number;
  conversations: number;
  latestTs: number;
}

/**
 * Aggregate the merged notification feed into one entry per app package
 * for privacy mode. `calls` = `category === "call"` notifications,
 * `regular` = the remaining non-conversation notifications, and
 * `conversations` = threads. Recency-sorted newest-first. Pure — no
 * `Date.now()`, no DOM, no i18n. Caller resolves the count string.
 */
export function buildPrivacyGroups(
  callNotifications: DecryptedNotification[],
  regularNotifications: DecryptedNotification[],
  conversations: ConversationThread[]
): PrivacyAppGroup[] {
  const acc = new Map<string, PrivacyAccumulator>();

  const bump = (
    key: string,
    app: string,
    ts: number,
    bucket: "calls" | "regular" | "conversations"
  ) => {
    const existing = acc.get(key);
    if (existing) {
      existing[bucket] += 1;
      if (ts > existing.latestTs) existing.latestTs = ts;
    } else {
      const next: PrivacyAccumulator = {
        appName: app,
        calls: 0,
        regular: 0,
        conversations: 0,
        latestTs: ts,
      };
      next[bucket] = 1;
      acc.set(key, next);
    }
  };

  for (const n of callNotifications) bump(n.appPackage, n.app, n.timestamp, "calls");
  for (const n of regularNotifications) bump(n.appPackage, n.app, n.timestamp, "regular");
  for (const c of conversations)
    bump(c.packageName, c.app, c.lastMessageTimestamp, "conversations");

  const groups: PrivacyAppGroup[] = [];
  for (const [packageKey, a] of acc) {
    const count = a.calls + a.regular + a.conversations;
    let labelKind: PrivacyAppGroup["labelKind"];
    if (a.conversations === count) labelKind = "messages";
    else if (a.calls === count) labelKind = "calls";
    else labelKind = "notifications";
    groups.push({ packageKey, appName: a.appName, count, labelKind, latestTs: a.latestTs });
  }

  groups.sort((x, y) => y.latestTs - x.latestTs);
  return groups;
}
